import { useState, useEffect, useCallback, useMemo } from "react";
import { Select, Input, Tag, Switch, Drawer, DatePicker, message, Spin } from "antd";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { InboxOutlined, LoadingOutlined } from "@ant-design/icons";
import { Area } from "@ant-design/plots";
import moment from "moment";

import { ORG_ID, DOMAIN_ID } from "../../constants";
import { logOutUser } from "../../common";
import { useDebounce } from "../../common/debounce";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../design-system/tokens";
import { useAnTheme } from "../../theme/AnThemeProvider";
import { useScrollbarWhileScrolling } from "../../design-system/tableUi";
import {
  dispatchGetAuthLog,
  dispatchGetUserAuthLog,
  dispatchGetAuthBadSources,
} from "../../utils/apiservice";
import { decisionMeta, errorName, bucketTimeline } from "../../utils/authLog";
import { getMfaMode } from "../../config/sidebarControls/mode/mfa";
import { mfaApiReasonLabel } from "../../pages/applications/appReasons";

const { RangePicker } = DatePicker;

const loadingIcon = <LoadingOutlined style={{ fontSize: 22, color: AN_COLORS.primary }} spin />;

const FilterField = ({ label, children }) => (
  <div>
    <div
      style={{
        fontFamily: AN_MONO,
        fontSize: 10.5,
        letterSpacing: 1.1,
        textTransform: "uppercase",
        color: AN_COLORS.muted,
        marginBottom: 6,
        fontWeight: 500,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <InboxOutlined style={{ fontSize: "24px", color: "#999" }} />
      <span>No authentication events</span>
    </div>
  </GridOverlay>
);

const DecisionTag = ({ decision }) => {
  const meta = decisionMeta(decision);
  return (
    <Tag
      style={{
        color: meta.color,
        background: `${anAlpha(meta.color, 8)}`,
        border: `1px solid ${anAlpha(meta.color, 20)}`,
        borderRadius: 6,
        fontWeight: 500,
        margin: 0,
      }}
    >
      {meta.label}
    </Tag>
  );
};

const gridSx = {
  border: "none",
  fontFamily: AN_FONT,
  fontSize: 13,
  color: AN_COLORS.ink,
  "& .MuiDataGrid-columnHeaders": {
    background: AN_COLORS.bg,
    borderBottom: `1px solid ${AN_COLORS.line2}`,
    fontFamily: AN_MONO,
    fontSize: 10.5,
    letterSpacing: 1.1,
    textTransform: "uppercase",
    color: AN_COLORS.muted,
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 500 },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${AN_COLORS.line2}`,
    color: AN_COLORS.ink2,
  },
  "& .MuiDataGrid-row:hover": { background: AN_COLORS.bg },
  "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${AN_COLORS.line2}` },
};

// Protocol values as gateways write them to did.auth_log.protocol: lowercase,
// compared server-side with `=` (GetAuthLog: "protocol = ?"), so the option
// value must be the exact stored string — "NTLM" would match nothing.
//
// GetAuthLog's `mode` limits the log to one MFA mode's protocols; the dropdown
// offers only those. RADIUS has one protocol, so no dropdown at all. (SSH
// lives in PAM mode, which shows the SSH access log instead of this page.)
const PROTOCOL_OPTIONS_BY_MODE = {
  ad: [
    { value: "kerberos", label: "Kerberos" },
    { value: "ntlm", label: "NTLM" },
    { value: "ldap", label: "LDAP" },
    { value: "smb", label: "SMB" },
  ],
  radius: [],
  // Applications: AuthNull as the identity provider (internal/idp audit.go).
  // The app's name is in realm, its client ID or SAML entity ID in serviceSpn.
  apps: [
    { value: "oidc", label: "OpenID Connect" },
    { value: "saml", label: "SAML" },
    { value: "mfa_api", label: "MFA API" },
  ],
};

// Modes GetAuthLog does not know yet (its ModeProtocols has ad, radius and
// pam; any other mode is a 400). Sent without `mode` — which is every
// protocol — so one of the mode's protocols is always picked instead.
const PROTOCOL_REQUIRED_MODES = { apps: true };

const rowId = (e, i) =>
  `${e.correlationId || "x"}-${e.decision || "d"}-${e.tsUnixMs || 0}-${i}`;

const AuthEventLogs = ({ mode = "ad" }) => {
  const protocolOptions = PROTOCOL_OPTIONS_BY_MODE[mode] || [];
  const protocolRequired = !!PROTOCOL_REQUIRED_MODES[mode] && protocolOptions.length > 0;
  const isApps = mode === "apps";
  const [notificationApi, notificationContext] = message.useMessage();
  // capture: the scroll happens on DataGrid's own scrollbar element, a
  // descendant — scroll events don't bubble, but they do capture.
  const gridScrollRef = useScrollbarWhileScrolling({ capture: true });

  // Per-product heading: e.g. "AD Authentication Logs", "Radius Authentication Logs".
  const modeLabel = getMfaMode(localStorage.getItem("currentMode"))?.label;
  const pageTitle = "Access Logs";
  const breadcrumb = `Admin · ${modeLabel ? modeLabel + " · " : ""}Logs`;

  // Filters
  const [principalInput, setPrincipalInput] = useState("");
  const debouncedPrincipal = useDebounce(principalInput, 500);
  const [protocol, setProtocol] = useState(protocolRequired ? protocolOptions[0].value : undefined);
  const [decision, setDecision] = useState(undefined);
  const [range, setRange] = useState(null);
  const [excludeIntermediate, setExcludeIntermediate] = useState(false);
  const [excludeBenignNoise, setExcludeBenignNoise] = useState(false);

  // Data
  const [events, setEvents] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });

  // Drawer (per-user drill-down)
  const [drawerPrincipal, setDrawerPrincipal] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [userEvents, setUserEvents] = useState([]);
  const [badSources, setBadSources] = useState([]);

  const handleLogout = useCallback(() => {
    notificationApi.error("Session expired, please sign in again.");
    logOutUser();
  }, [notificationApi]);

  const fetchAuthLog = useCallback(() => {
    setIsLoading(true);
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      page: pagination.page + 1, // API is 1-based
      pageSize: pagination.pageSize,
    };
    if (!protocolRequired) payload.mode = mode;
    if (debouncedPrincipal) payload.principal = debouncedPrincipal.trim();
    if (protocol) payload.protocol = protocol;
    if (decision) payload.decision = decision;
    if (range && range[0]) payload.sinceUnixMs = range[0].valueOf();
    if (range && range[1]) payload.untilUnixMs = range[1].valueOf();
    if (excludeIntermediate) payload.excludeIntermediate = true;
    if (excludeBenignNoise) payload.excludeBenignNoise = true;

    dispatchGetAuthLog({
      payload,
      onSuccess: (data) => {
        setIsLoading(false);
        setEvents(data?.events || []);
        setTimeline(data?.timeline || []);
        setTotal(data?.total || 0);
      },
      onFailure: ({ message: msg, isLogout }) => {
        setIsLoading(false);
        if (isLogout) return handleLogout();
        notificationApi.error(msg || "Could not load authentication logs.");
      },
      onError: ({ message: msg }) => {
        setIsLoading(false);
        notificationApi.error(msg || "Could not load authentication logs.");
      },
    });
  }, [
    mode,
    protocolRequired,
    pagination,
    debouncedPrincipal,
    protocol,
    decision,
    range,
    excludeIntermediate,
    excludeBenignNoise,
    notificationApi,
    handleLogout,
  ]);

  useEffect(() => {
    fetchAuthLog();
  }, [fetchAuthLog]);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedPrincipal,
    protocol,
    decision,
    range,
    excludeIntermediate,
    excludeBenignNoise,
  ]);

  const openDrawer = useCallback(
    (principal) => {
      if (!principal) return;
      setDrawerPrincipal(principal);
      setDrawerLoading(true);
      setUserEvents([]);
      setBadSources([]);
      const base = { orgId: ORG_ID(), tenantId: DOMAIN_ID(), principal };

      dispatchGetUserAuthLog({
        payload: base,
        onSuccess: (data) => {
          setUserEvents(data?.events || []);
          setDrawerLoading(false);
        },
        onFailure: ({ isLogout }) => {
          setDrawerLoading(false);
          if (isLogout) handleLogout();
        },
        onError: () => setDrawerLoading(false),
      });

      dispatchGetAuthBadSources({
        payload: base,
        onSuccess: (data) => setBadSources(data?.sources || []),
        onFailure: ({ isLogout }) => {
          if (isLogout) handleLogout();
        },
        onError: () => {},
      });
    },
    [handleLogout]
  );

  const columns = useMemo(
    () => [
      {
        field: "tsUnixMs",
        headerName: "Time",
        flex: 1.2,
        minWidth: 175,
        renderCell: ({ value }) =>
          value ? moment(value).format("MMM D, YYYY HH:mm:ss") : "—",
      },
      {
        field: "principal",
        headerName: "Principal",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) =>
          value ? (
            <button
              type="button"
              onClick={() => openDrawer(value)}
              style={{
                border: "none",
                background: "transparent",
                color: AN_COLORS.primary,
                cursor: "pointer",
                padding: 0,
                fontFamily: AN_FONT,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {value}
            </button>
          ) : (
            "—"
          ),
      },
      {
        field: "decision",
        headerName: "Decision",
        flex: 0.9,
        minWidth: 120,
        renderCell: ({ value }) => <DecisionTag decision={value} />,
      },
      { field: "protocol", headerName: "Protocol", flex: 0.7, minWidth: 105 },
      {
        field: "clientIp",
        headerName: "Client IP",
        flex: 0.9,
        minWidth: 145,
        renderCell: ({ value }) => (
          <span style={{ fontFamily: AN_MONO }}>{value || "—"}</span>
        ),
      },
      { field: "realm", headerName: isApps ? "Application" : "Realm", flex: 1, minWidth: 150 },
      ...(isApps
        ? [
            {
              field: "serviceSpn",
              headerName: "Client / entity ID",
              flex: 1,
              minWidth: 220,
              renderCell: ({ value }) => (
                <span title={value || undefined} style={{ fontFamily: AN_MONO, color: AN_COLORS.muted }}>
                  {value || "—"}
                </span>
              ),
            },
          ]
        : []),
      {
        field: "reason",
        headerName: "Reason / Error",
        flex: 1.1,
        minWidth: 200,
        renderCell: ({ row }) => {
          const err = errorName(row.upstreamErrorCode);
          // MFA API sign-ins: the reason code in words (the code stays in the tooltip).
          const label = row.protocol === "mfa_api" ? mfaApiReasonLabel(row.reason) : "";
          return (
            <span title={[row.reason, err].filter(Boolean).join(" ") || undefined}>
              {label || row.reason || "—"}
              {err ? (
                <span style={{ color: AN_COLORS.danger, fontFamily: AN_MONO, marginLeft: 6 }}>
                  {err}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        // Correlation IDs are long (hostname + GUID). Give them enough room to
        // read without truncating; the grid scrolls horizontally when the
        // viewport can't fit every column.
        field: "correlationId",
        headerName: "Correlation",
        flex: 0.9,
        minWidth: 290,
        renderCell: ({ value }) => (
          <span title={value || undefined} style={{ fontFamily: AN_MONO, color: AN_COLORS.muted }}>
            {value || "—"}
          </span>
        ),
      },
    ],
    [openDrawer, isApps]
  );

  const rows = useMemo(() => events.map((e, i) => ({ id: rowId(e, i), ...e })), [events]);

  const chartData = useMemo(() => bucketTimeline(timeline), [timeline]);

  // The chart draws on a canvas, which cannot read the CSS variables.
  const { palette: chartPalette, mode: chartMode } = useAnTheme();
  const chartConfig = {
    data: chartData,
    xField: "label",
    yField: "count",
    height: 240,
    autoFit: true,
    shapeField: "smooth",
    style: { fill: `l(270) 0:${chartPalette.primary}00 1:${chartPalette.primary}55` },
    line: { style: { stroke: chartPalette.primary, strokeWidth: 2 } },
    theme: chartMode === "dark" ? "classicDark" : "classic",
    axis: { x: { labelSpacing: 4 } },
    legend: false,
    tooltip: { title: "label", items: [{ field: "count", name: "Events" }] },
  };

  const cardStyle = {
    background: AN_COLORS.surface,
    border: `1px solid ${AN_COLORS.line}`,
    borderRadius: 10,
    padding: 16,
    marginBottom: 14,
  };

  return (
    <>
      {notificationContext}
      <div style={{ padding: "24px 28px", fontFamily: AN_FONT, color: AN_COLORS.ink }}>
        {/* Header */}
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            {breadcrumb}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: -0.4,
              color: AN_COLORS.ink,
            }}
          >
            {pageTitle}
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            {total.toLocaleString()} authentication events. Use filters to narrow the timeline.
          </div>
        </div>

        {/* Timeline */}
        <div style={cardStyle}>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.1,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 12,
              fontWeight: 500,
            }}
          >
            Authentication activity
          </div>
          {chartData.length ? (
            <Area {...chartConfig} />
          ) : (
            <div
              style={{
                height: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: AN_COLORS.muted,
                fontSize: 13,
              }}
            >
              No activity for the selected filters.
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={cardStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              alignItems: "end",
            }}
          >
            <FilterField label="Principal">
              <Input
                allowClear
                placeholder="Search principal"
                value={principalInput}
                onChange={(e) => setPrincipalInput(e.target.value)}
                style={{ height: 38 }}
              />
            </FilterField>
            {protocolOptions.length > 0 && (
              <FilterField label="Protocol">
                <Select
                  allowClear={!protocolRequired}
                  style={{ width: "100%", height: 38 }}
                  placeholder="All protocols"
                  value={protocol}
                  onChange={(v) => setProtocol(protocolRequired ? v || protocolOptions[0].value : v)}
                  options={protocolOptions}
                />
              </FilterField>
            )}
            <FilterField label="Decision">
              <Select
                allowClear
                style={{ width: "100%", height: 38 }}
                placeholder="All decisions"
                value={decision}
                onChange={setDecision}
                options={[
                  { value: "allow", label: "Allow" },
                  { value: "deny", label: "Deny" },
                  { value: "mfa_required", label: "MFA required" },
                ]}
              />
            </FilterField>
            <FilterField label="Time range">
              <RangePicker
                showTime
                style={{ width: "100%", height: 38 }}
                value={range}
                onChange={setRange}
              />
            </FilterField>
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 14, alignItems: "center" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <Switch
                size="small"
                checked={excludeIntermediate}
                onChange={setExcludeIntermediate}
              />
              Exclude intermediate
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <Switch
                size="small"
                checked={excludeBenignNoise}
                onChange={setExcludeBenignNoise}
              />
              Exclude benign noise
            </label>
          </div>
        </div>

        {/* Table. an-grid-x restores the horizontal scrollbar that the global
            .MuiDataGrid-scrollbar override in index.css disables, and only
            shows it while the grid is being scrolled. */}
        <div
          ref={gridScrollRef}
          className="an-grid-x"
          style={{
            background: AN_COLORS.surface,
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            loading={isLoading}
            getRowId={(row) => row.id}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            autoHeight
            components={{
              NoRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
            }}
            sx={gridSx}
          />
        </div>
      </div>

      {/* Per-user drawer */}
      <Drawer
        title={
          <span style={{ fontFamily: AN_FONT }}>
            Authentication detail ·{" "}
            <span style={{ color: AN_COLORS.primary }}>{drawerPrincipal}</span>
          </span>
        }
        width={680}
        open={!!drawerPrincipal}
        onClose={() => setDrawerPrincipal(null)}
      >
        {drawerLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Spin indicator={loadingIcon} />
          </div>
        ) : (
          <div style={{ fontFamily: AN_FONT }}>
            {/* Failing sources */}
            <div
              style={{
                fontFamily: AN_MONO,
                fontSize: 11,
                letterSpacing: 1.1,
                textTransform: "uppercase",
                color: AN_COLORS.muted,
                marginBottom: 8,
                fontWeight: 500,
              }}
            >
              Failing sources
            </div>
            {badSources.length ? (
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: AN_COLORS.muted, fontSize: 12 }}>
                    <th style={{ padding: "6px 8px" }}>Client IP</th>
                    <th style={{ padding: "6px 8px" }}>Failures</th>
                    <th style={{ padding: "6px 8px" }}>Last seen</th>
                    <th style={{ padding: "6px 8px" }}>Last error</th>
                  </tr>
                </thead>
                <tbody>
                  {badSources.map((s) => (
                    <tr key={s.clientIp} style={{ borderTop: `1px solid ${AN_COLORS.line2}` }}>
                      <td style={{ padding: "8px", fontFamily: AN_MONO }}>{s.clientIp}</td>
                      <td style={{ padding: "8px", color: AN_COLORS.danger, fontWeight: 600 }}>
                        {s.count}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {s.lastTsUnixMs ? moment(s.lastTsUnixMs).format("MMM D, HH:mm") : "—"}
                      </td>
                      <td style={{ padding: "8px", fontFamily: AN_MONO }}>
                        {s.lastErrorName || errorName(s.lastErrorCode) || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: AN_COLORS.muted, fontSize: 13, marginBottom: 24 }}>
                No failing sources for this principal.
              </div>
            )}

            {/* Per-user timeline */}
            <div
              style={{
                fontFamily: AN_MONO,
                fontSize: 11,
                letterSpacing: 1.1,
                textTransform: "uppercase",
                color: AN_COLORS.muted,
                marginBottom: 8,
                fontWeight: 500,
              }}
            >
              Recent events ({userEvents.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {userEvents.map((e, i) => (
                <div
                  key={rowId(e, i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    border: `1px solid ${AN_COLORS.line2}`,
                    borderRadius: 8,
                  }}
                >
                  <DecisionTag decision={e.decision} />
                  <span style={{ fontSize: 12, color: AN_COLORS.muted, minWidth: 130 }}>
                    {moment(e.tsUnixMs).format("MMM D, HH:mm:ss")}
                  </span>
                  <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{e.clientIp}</span>
                  <span style={{ fontSize: 12, color: AN_COLORS.muted }}>{e.protocol}</span>
                  {errorName(e.upstreamErrorCode) ? (
                    <span style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.danger }}>
                      {errorName(e.upstreamErrorCode)}
                    </span>
                  ) : null}
                </div>
              ))}
              {!userEvents.length && (
                <div style={{ color: AN_COLORS.muted, fontSize: 13 }}>No events found.</div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default AuthEventLogs;
